/**
 * What a person is writing, and where it comes from.
 *
 * Everything here is pure: a draft is made from the message being answered, and
 * the surface in `compose.ts` only edits it. The From address is not part of it
 * — it comes from the account, and postrd refuses a request whose sealing key
 * does not hold the address it names.
 */
import type { BlobRef, MailPayload } from "../nostr/seal";
import type { OutboxRequest } from "../nostr/outbox";
import type { DmDeliveryState } from "../nostr/dmsend";
import { nip19 } from "nostr-tools";

export type DraftKind = "new" | "reply" | "replyAll" | "forward";

export interface Draft {
  /** Stable key: one draft per message answered, and one for a new message. */
  id: string;
  kind: DraftKind;
  /** Raw text, as typed. Addresses are parsed when the message is sent. */
  to: string;
  cc: string;
  subject: string;
  body: string;
  /** The Message-Id this answers, with no angle brackets. */
  inReplyTo?: string;
  /** The thread above it, oldest first. */
  references?: string[];
  /** Files carried by reference, never re-uploaded. Forward only. */
  attachments?: BlobRef[];
  /** The stored message this came from, so the reader can be returned to. */
  source?: string;
  /** Set when the answer goes back over Nostr, because that is how it came. */
  via?: "nostr";
  /** A DM's recipients, hex. `to` is only what the person reads. */
  recipients?: string[];
  /** The same rumor and wraps allow a partial DM send to resume safely. */
  dmDelivery?: DmDeliveryState;
  /**
   * When a send of this draft began, cleared when it settles. Still set after a
   * reload means the page closed mid-send and the message may already be out.
   */
  sendingSince?: number;
  updated: number;
}

/** What the draft needs from a message. `StoredMail` satisfies it. */
export interface Answerable extends MailPayload {
  id: string;
  dateMs?: number;
  origin?: "email" | "nostr";
  sender?: string;
  sender_pubkey?: string;
}

export function newDraft(): Draft {
  return { id: "new", kind: "new", to: "", cc: "", subject: "", body: "", updated: Date.now() };
}

/**
 * Answer one message.
 *
 * Reply goes to the sender alone. Reply to all adds everybody the message
 * names, minus this account — a reply that copies you is a message you will
 * read twice.
 *
 * Answering a message this account sent answers the people it went to. The
 * conversation view makes that reachable: a message you wrote is in the thread,
 * and the sender of it is you.
 */
export function replyDraft(item: Answerable, account: string, all = false): Draft {
  const sender = addressOf(item.from);
  const mine = item.sent === true;
  const others = all || mine ? (item.to ?? []).map(addressOf) : [];
  const to = unique([sender, ...others].filter((a) => a && !same(a, account)));
  const cc = all
    ? unique((item.cc ?? []).map(addressOf).filter((a) => a && !same(a, account) && !to.includes(a)))
    : [];
  const draft: Draft = {
    id: `${all ? "reply-all" : "reply"}:${item.id}`,
    kind: all ? "replyAll" : "reply",
    // With nobody but this account on the message, the sender is still the
    // person to answer, even when that is the account itself.
    to: (to.length > 0 ? to : [sender]).join(", "),
    cc: cc.join(", "),
    subject: prefixed(item.subject, "Re:"),
    body: `\n\n${quote(item)}`,
    references: thread(item),
    source: item.id,
    updated: Date.now(),
  };
  const parent = bare(item.message_id);
  if (parent) draft.inReplyTo = parent;
  return draft;
}

/**
 * Answer a DM, over Nostr.
 *
 * A chat is not quoted: the conversation is on screen above the reply. The
 * subject is the conversation's title under NIP-17, so it is kept as it is.
 */
export function dmReplyDraft(item: Answerable, ownPubkey: string, all = false): Draft {
  const named = (item.to ?? []).map(pubkeyOf).filter(Boolean);
  const people = item.sent ? named : [item.sender_pubkey ?? "", ...(all ? named : [])];
  const recipients = [...new Set(people.filter((pk) => pk && pk !== ownPubkey))];
  const draft: Draft = {
    id: `${all ? "reply-all" : "reply"}:${item.id}`,
    kind: all ? "replyAll" : "reply",
    to: recipients.map((pk) => (pk === item.sender_pubkey && item.sender ? item.sender : nip19.npubEncode(pk))).join(", "),
    cc: "",
    subject: item.subject ?? "",
    body: "",
    source: item.id,
    via: "nostr",
    recipients,
    updated: Date.now(),
  };
  if (item.message_id) draft.inReplyTo = item.message_id;
  return draft;
}

function pubkeyOf(npub: string): string {
  try {
    const decoded = nip19.decode(npub);
    return decoded.type === "npub" ? decoded.data : "";
  } catch {
    return "";
  }
}

/**
 * Pass one message on.
 *
 * The body is quoted and the files travel as references: they are already in
 * the blob store, postrd can read them, and a rumor has about 40KB for
 * everything. An embedded image is left behind, because the body it belongs to
 * is being quoted as text and a `cid:` with nothing to point at is worse than
 * no image at all.
 */
export function forwardDraft(item: Answerable): Draft {
  return {
    id: `forward:${item.id}`,
    kind: "forward",
    to: "",
    cc: "",
    subject: prefixed(item.subject, "Fwd:"),
    body: `\n\n${forwarded(item)}`,
    attachments: (item.attachments ?? []).filter((file) => !file.inline),
    source: item.id,
    updated: Date.now(),
  };
}

/** True when the person has written nothing worth keeping. */
export function isEmpty(draft: Draft): boolean {
  return [draft.to, draft.cc, draft.subject, draft.body].every((field) => field.trim() === "");
}

/**
 * Addresses out of a line of text.
 *
 * Commas and semicolons both separate, because both are typed, and a display
 * name is dropped: what goes on the wire is the address.
 */
export function addresses(line: string): string[] {
  return unique(
    line
      .split(/[,;\n]/)
      .map((part) => addressOf(part))
      .filter(Boolean),
  );
}

/**
 * Why this draft cannot be sent, or null when it can.
 *
 * The From address is checked first because nothing else matters without it:
 * postrd refuses a request whose key does not hold the address, and the client
 * only learns the address from mail it already holds.
 */
export function whyNotSendable(draft: Draft, account: string): string | null {
  if (draft.via === "nostr") {
    if (!draft.recipients || draft.recipients.length === 0) return "This DM has nobody to go to.";
    return draft.body.trim() === "" ? "Write something to send." : null;
  }
  if (!account) return "This client does not know your address yet. It learns it from the mail you hold.";
  const to = addresses(draft.to);
  if (to.length === 0) return "Name somebody to send this to.";
  const bad = [...to, ...addresses(draft.cc)].find((a) => !isAddress(a));
  if (bad) return `${bad} is not an email address.`;
  if (draft.body.trim() === "" && draft.subject.trim() === "") {
    return "This message has no subject and no body.";
  }
  return null;
}

/** The request postrd reads. The From address comes from the account. */
export function toRequest(draft: Draft, account: string): OutboxRequest {
  const request: OutboxRequest = {
    from: account,
    to: addresses(draft.to),
    subject: draft.subject.trim(),
    text: draft.body.trim(),
  };
  const cc = addresses(draft.cc);
  if (cc.length > 0) request.cc = cc;
  if (draft.inReplyTo) request.in_reply_to = draft.inReplyTo;
  if (draft.references && draft.references.length > 0) request.references = draft.references;
  if (draft.attachments && draft.attachments.length > 0) request.attachments = draft.attachments;
  return request;
}

/**
 * The address this account sends from.
 *
 * Sealed account addresses take priority. The first configured domain is
 * primary, and an address on any other configured domain is sent as the same
 * name on the primary domain: the other domains are aliases that still receive
 * mail but can no longer send it. The registry holds each owner's key under
 * both addresses.
 */
export function accountAddress(mail: readonly Answerable[], domains: readonly string[]): string {
  return onPrimary(foundAddress(mail, domains), domains);
}

function onPrimary(address: string, domains: readonly string[]): string {
  const primary = domains[0]?.trim().toLowerCase();
  const at = address.lastIndexOf("@");
  if (!primary || at <= 0) return address;
  const domain = address.slice(at + 1);
  const alias = domains.slice(1).some((d) => d.trim().toLowerCase() === domain);
  return alias ? `${address.slice(0, at)}@${primary}` : address;
}

function foundAddress(mail: readonly Answerable[], domains: readonly string[]): string {
  const named = mail.flatMap((item) => item.account ? [item.account.trim().toLowerCase()] : []);
  for (const domain of domains) {
    const address = named.find((value) => value.endsWith(`@${domain.trim().toLowerCase()}`));
    if (address) return address;
  }
  if (named[0]) return named[0];

  const local = (address: string): boolean =>
    domains.some((domain) => address.endsWith(`@${domain.trim().toLowerCase()}`));

  const counted = new Map<string, number>();
  for (const item of mail) {
    const candidates = item.sent
      ? [addressOf(item.from)]
      : [...(item.to ?? []), ...(item.cc ?? [])].map(addressOf);
    for (const address of candidates) {
      if (address && local(address)) counted.set(address, (counted.get(address) ?? 0) + 1);
    }
  }
  const best = [...counted.entries()].sort((a, b) => b[1] - a[1])[0];
  return best?.[0] ?? "";
}

/** `Name <addr>` down to `addr`, lowercased. Empty when there is no address. */
export function addressOf(header: string): string {
  const angled = /<([^>]+)>/.exec(header ?? "");
  const raw = (angled?.[1] ?? header ?? "").trim().toLowerCase();
  return raw.includes("@") ? raw.replace(/^mailto:/, "") : "";
}

function isAddress(address: string): boolean {
  return /^[^\s@,;<>"]+@[^\s@,;<>".]+\.[^\s@,;<>"]+$/.test(address);
}

function same(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function unique(list: string[]): string[] {
  return [...new Set(list.map((a) => a.trim().toLowerCase()).filter(Boolean))];
}

/** `Re: Re: thing` is one `Re:`, however many the thread has collected. */
function prefixed(subject: string, prefix: string): string {
  const clean = (subject ?? "").replace(/^\s*((re|fwd|fw)\s*:\s*)+/i, "").trim();
  return `${prefix} ${clean}`.trim();
}

function bare(messageID: string | undefined): string | undefined {
  const id = (messageID ?? "").trim().replace(/^<|>$/g, "");
  return id || undefined;
}

/** The thread above the reply: what the message listed, then the message. */
function thread(item: Answerable): string[] {
  const above = [...(item.references ?? []), item.in_reply_to ?? "", item.message_id ?? ""];
  return unique(above.map((id) => id.trim().replace(/^<|>$/g, "")));
}

/** The body of the message being answered, as text, prefixed line by line. */
export function quote(item: Answerable): string {
  const body = bodyText(item);
  const head = `On ${stamp(item)}, ${who(item)} wrote:`;
  if (!body) return head;
  return `${head}\n${body.split("\n").map((line) => `> ${line}`).join("\n")}`;
}

/** The same body under the headers a forward carries, which are the point. */
export function forwarded(item: Answerable): string {
  const lines = [
    "---------- Forwarded message ----------",
    `From: ${item.from}`,
    `Date: ${stamp(item)}`,
    `Subject: ${item.subject || "(no subject)"}`,
  ];
  if (item.to && item.to.length > 0) lines.push(`To: ${item.to.join(", ")}`);
  return `${lines.join("\n")}\n\n${bodyText(item)}`;
}

/**
 * The words of a message, whatever shape it arrived in.
 *
 * An HTML-only message is flattened here rather than quoted as markup: the
 * compose surface writes text, and a quoted `<table>` is not a quotation.
 */
export function bodyText(item: Answerable): string {
  const text = (item.text ?? "").trim();
  if (text) return text;
  const html = (item.html ?? "").trim();
  return html ? textFromHtml(html) : "";
}

/**
 * Markup to words.
 *
 * Parsed inert, exactly as the reader parses it, so nothing in a sender's
 * message runs or fetches on the way into a reply.
 */
export function textFromHtml(html: string): string {
  const parsed = new DOMParser().parseFromString(html, "text/html");
  for (const node of parsed.querySelectorAll("script, style, head")) node.remove();
  for (const node of parsed.querySelectorAll("br")) node.replaceWith("\n");
  for (const node of parsed.querySelectorAll("p, div, tr, li, h1, h2, h3, h4, h5, h6")) {
    node.append("\n");
  }
  return (parsed.body.textContent ?? "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function who(item: Answerable): string {
  const name = /^\s*(.*?)\s*<[^>]*>\s*$/.exec(item.from ?? "")?.[1]?.replace(/^["']|["']$/g, "");
  return (name || addressOf(item.from) || item.from || "someone").trim();
}

function stamp(item: Answerable): string {
  const ms = item.dateMs ?? Date.parse(item.date ?? "");
  if (!ms || Number.isNaN(ms)) return "an unknown date";
  const date = new Date(ms);
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
